import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable, map, switchMap, throwError} from 'rxjs';
import {QINVENTORY_BASE_URL} from './config';

export interface StockMovement {
  transactionId: string;
  type: string;
  quantity: number;
  productId: string;
  locationId: string;
  sourceId: string;
  destinationId: string;
  createdAt: string;
  updatedAt: string;
  change: number;
  direction: 'in' | 'out' | 'none';
  balanceBefore: number;
  balanceAfter: number;
}

export class InvalidStockMovementResponseError extends Error {
  constructor() {
    super('History was returned, but its movement and balance details are ' +
      'missing or invalid. Stock changes cannot be displayed.');
    this.name = 'InvalidStockMovementResponseError';
  }
}

@Injectable({providedIn: 'root'})
export class ShopStockHistoryService {
  constructor(
    private http: HttpClient,
    @Inject(QINVENTORY_BASE_URL) private baseUrl: string,
  ) {}

  load(sku: string, locationId: string): Observable<StockMovement[]> {
    const exactSku = sku.trim();
    locationId = locationId.trim();
    if (!exactSku || !locationId.trim()) {
      return throwError(() => new Error('Choose an exact SKU and location.'));
    }
    // A stock-aggregate row ID is not an inventory product ID.
    return this.http.post<{
      body: {id: string; sku: string}[] | null; totalData: number;
    }>(this.baseUrl + '/products', {
      skus: [exactSku], pageNumber: 0, pageSize: 2,
    }).pipe(
        switchMap((result) => {
          const products = result.body ?? [];
          if (result.totalData !== 1 || products.length !== 1 ||
              products[0].sku !== exactSku || !products[0].id) {
            return throwError(() => new Error('Inventory product not uniquely found.'));
          }
          const productId = products[0].id;
          return this.http.post<{body: StockMovement[]; totalData: number}>(
              this.baseUrl + '/inventory-transactions', {productId, locationId},
          ).pipe(map((response) => {
            // Fail closed if an older deployment ignores locationId and returns
            // the legacy product-history shape without movement balances.
            if (!Array.isArray(response.body) || response.body.some((row) =>
              row.productId !== productId || !row.transactionId ||
              !(row.type === 'transfer' ?
                [row.sourceId, row.destinationId].includes(locationId) :
                row.locationId === locationId) ||
              ![row.change, row.balanceBefore, row.balanceAfter, row.quantity]
                  .every((value) =>
                    typeof value === 'number' && Number.isFinite(value)) ||
              !['in', 'out', 'none'].includes(row.direction) ||
              !Number.isFinite(Date.parse(row.createdAt)))) {
              throw new InvalidStockMovementResponseError();
            }
            // Preserve server order, including its equal-time tie-breaker.
            return response.body;
          }));
        }),
    );
  }
}
